import { useContext, useEffect, useState } from 'react';
import { FastSpringPaymentContext } from '../../../context/FastSpringPaymentContext';
import useQueryMemo from '@/common/hooks/urlMemo';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { IpStatsContext } from '@/common/contexts/IpStatsContext';
import { CheckoutContext } from '@/common/contexts/checkoutContext';
import { useMutation } from 'react-query';
import { getCurrentPriceConfig } from '@/api/query/price-config';
import { getUtms } from '@/common/helpers/getUtms';
import { getCountryFromLang } from '@/common/helpers/getCountryFromLang';

const usePlanHook = () => {
	const { promo, email, isTest } = useContext(FastSpringPaymentContext);
	const queryMemo = useQueryMemo();

	const { lang } = useContext(LanguageContext);
	const { handleSendIPstats, handleFbStats } = useContext(IpStatsContext);
	const { quizVersion, spVersion } = useContext(CheckoutContext);

	const [pricesData, setPricesData] = useState([]);
	const [selectedOption, setSelectedOption] = useState();

	const handleLoadPricesMutation = useMutation({
		mutationFn: (value) => getCurrentPriceConfig(value),
		onSuccess: ({ data }) => {
			const prices = data.prices.sort((a, b) => a.full_price - b.full_price);
			const plan = localStorage.getItem('plan');
			const selected =
				prices?.find((p) => p?.type?.value === plan) || prices[0];
			setPricesData(() => prices);
			setSelectedOption(() => selected);
		},
	});

	const handleChangeSelect = (pd) => {
		setSelectedOption(() => pd);
	};

	const handleCreateOrder = (data) => {
		const additionalCheckoutText = isTest ? '_CHECKOUT_D' : '';

		handleFbStats('InitiateCheckout', {});
		handleFbStats('AddPaymentInfo', {});

		sendPaymentIpStat();

		window?.gtag('event', 'initiate_checkout', {});
		window?.gtag('event', 'add_payment_info', {});

		window?.fbq('track', 'InitiateCheckout');
		window?.fbq('track', 'AddPaymentInfo');

		window?.uetq?.push('event', 'subscribe');

		let currentVersion = localStorage.getItem('quiz_variant') || 'LP3';
		const quizLength = window.localStorage.getItem('quiz_length_version') || '';

		const spPageVersion =
			queryMemo.get('sp_version') || localStorage.getItem('sp_version');
		const originVariant =
			queryMemo.get('origin_variant') || localStorage.getItem('origin_variant');
		localStorage.removeItem('origin_variant');
		let eventName;

		if (promo) {
			eventName = `PURCHASE_AD_${currentVersion}${quizLength}_PROMO${additionalCheckoutText}`;
		} else if (originVariant) {
			eventName = `${originVariant}_PURCHASE${additionalCheckoutText}`;
		} else {
			eventName =
				`PURCHASE_AD_${currentVersion}${quizLength}` +
				(spPageVersion ? `_${spPageVersion}` : '') +
				additionalCheckoutText;
		}
		handleSendIPstats(eventName);

		const price = promo
			? selectedOption.full_price
			: selectedOption.promo_full_price;
		const currency = 'brl';

		handleFbStats('Purchase', {
			currency: currency || 'PLN',
			value: price,
		});
		window?.gtag('set', 'user_data', {
			email: localStorage.getItem('quiz_email'),
		});
		window?.gtag('event', 'purchase', {
			currency: currency || 'PLN',
			value: price,
		});
		window?.gtag('event', 'conversion', {
			send_to: 'AW-16638689875/1akwCK70zMEZENOE-f09',
			value: price,
			currency: currency || 'PLN',
		});

		window?.fbq('track', 'Purchase', {
			currency: currency || 'PLN',
			value: price,
		});
		window?.uetq?.push('event', 'purchase', {
			currency: currency || 'PLN',
			revenue_value: price,
		});

		localStorage.removeItem('user_register');
		localStorage.removeItem('register_order');

		setTimeout(() => {
			window.location.href = `https://${window.location.host}/first-login`;
		}, 2000);
	};

	useEffect(() => {
		if (!pricesData.length) {
			const payload = {
				country: getCountryFromLang(lang),
			};

			handleLoadPricesMutation.mutate(payload);
		}
	}, []);

	const reloadFastSpring = () => {
		const old = document.getElementById('fsc-api');
		if (old) old.remove();

		const script = document.createElement('script');
		script.id = 'fsc-api';
		script.src =
			'https://sbl.onfastspring.com/sbl/1.0.5/fastspring-builder.min.js';
		script.type = 'text/javascript';
		script.dataset.storefront = 'synthi.onfastspring.com/embedded-br-pt';
		script.dataset.popupWebhookReceived = 'onCheckoutCompleted';

		document.body.appendChild(script);
	};

	const handleSetupPriceSum = async () => {
		const path = promo
			? selectedOption.fast_spring_product_promo_path
			: selectedOption.fast_spring_product_path;
		reloadFastSpring();

		await window.fastspring.builder.tag({
			paymentUtms: JSON.stringify(getUtms(queryMemo)),
			plan: selectedOption._id,
			lang: lang,
			promo: promo,
		});

		const spitedCountry = lang.split('-')[0];
		const spitedLang = lang.split('-')[1];

		await window.fastspring.builder.push({
			products: [{ path, quantity: 1 }],
			country: spitedCountry,
			language: spitedLang,
			paymentContact: {
				email: email,
			},
			checkout: true,
		});
	};

	const sendPaymentIpStat = () => {
		const additionalCheckoutText = isTest ? '_CHECKOUT_D' : '';

		handleSendIPstats(
			`AD_${quizVersion}_CHECKOUT_SUM${additionalCheckoutText}`,
		);

		if (promo) {
			handleSendIPstats(
				`AD_${quizVersion}_CHECKOUT_PROMO${additionalCheckoutText}`,
			);

			if (selectedOption.type.value === 'PREMIUM') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}6MO_PROMO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'PRO') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}3MO_PROMO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'BASIC') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}1MO_PROMO${additionalCheckoutText}`,
				);
			}
		} else {
			handleSendIPstats(`AD_${quizVersion}_CHECKOUT`);

			if (selectedOption.type.value === 'PREMIUM') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}6MO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'PRO') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}3MO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'BASIC') {
				handleSendIPstats(
					`AD_${quizVersion}_CHECKOUT_${spVersion}1MO${additionalCheckoutText}`,
				);
			}
		}
	};

	const handleSendJoinIpStat = () => {
		const additionalCheckoutText = isTest ? '_CHECKOUT_D' : '';

		handleSendIPstats(
			`AD_${quizVersion}_JOIN_CHECKOUT_SUM${additionalCheckoutText}`,
		);

		if (promo) {
			handleSendIPstats(
				`AD_${quizVersion}_JOIN_CHECKOUT_PROMO${additionalCheckoutText}`,
			);

			if (selectedOption.type.value === 'PREMIUM') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}6MO_PROMO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'PRO') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}3MO_PROMO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'BASIC') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}1MO_PROMO${additionalCheckoutText}`,
				);
			}
		} else {
			handleSendIPstats(
				`AD_${quizVersion}_JOIN_CHECKOUT${additionalCheckoutText}`,
			);

			if (selectedOption.type.value === 'PREMIUM') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}6MO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'PRO') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}3MO${additionalCheckoutText}`,
				);
			}
			if (selectedOption.type.value === 'BASIC') {
				handleSendIPstats(
					`AD_${quizVersion}_JOIN_CHECKOUT_${spVersion}1MO${additionalCheckoutText}`,
				);
			}
		}
	};

	useEffect(() => {
		if (!selectedOption || window?.fastspring?.builder === undefined) return;
		handleSetupPriceSum();
		handleSendJoinIpStat();
	}, [selectedOption]);

	useEffect(() => {
		if (!selectedOption) return;

		window.onCheckoutCompleted = (data) => {
			handleCreateOrder(data);
		};

		return () => {
			window.onCheckoutCompleted = () => {};
		};
	}, [selectedOption]);

	return {
		isLoading: handleLoadPricesMutation.isLoading,
		handleChangeSelect,
		handleCreateOrder,
		pricesData,
		promo,
		selectedOption,
	};
};

export default usePlanHook;
